import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { cn } from '../../lib/utils';

export interface MentionUser {
  id: string;
  name: string;
  email: string;
  avatarUrl: string | null;
  role: string;
}

interface MentionListProps {
  items: MentionUser[];
  command: (item: { id: string; label: string }) => void;
}

export interface MentionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

export const MentionList = forwardRef<MentionListRef, MentionListProps>(
  ({ items, command }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);

    const selectItem = (index: number) => {
      const item = items[index];
      if (item) {
        command({ id: item.id, label: item.name });
      }
    };

    const upHandler = () => {
      setSelectedIndex((selectedIndex + items.length - 1) % items.length);
    };

    const downHandler = () => {
      setSelectedIndex((selectedIndex + 1) % items.length);
    };

    const enterHandler = () => {
      selectItem(selectedIndex);
    };

    useEffect(() => {
      setSelectedIndex(0);
    }, [items]);

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (event.key === 'ArrowUp') {
          upHandler();
          return true;
        }

        if (event.key === 'ArrowDown') {
          downHandler();
          return true;
        }

        if (event.key === 'Enter') {
          enterHandler();
          return true;
        }

        return false;
      },
    }));

    if (items.length === 0) {
      return (
        <div className="bg-bg-secondary border border-border rounded-lg shadow-lg p-3 text-sm text-text-muted">
          No users found
        </div>
      );
    }

    return (
      <div className="bg-bg-secondary border border-border rounded-lg shadow-lg overflow-hidden max-h-64 overflow-y-auto">
        {items.map((item, index) => (
          <button
            key={item.id}
            type="button"
            className={cn(
              'flex items-center gap-3 w-full px-3 py-2 text-left transition-colors',
              index === selectedIndex
                ? 'bg-accent/10 text-accent'
                : 'text-text hover:bg-bg-tertiary'
            )}
            onClick={() => selectItem(index)}
          >
            {item.avatarUrl ? (
              <img
                src={item.avatarUrl}
                alt={item.name}
                className="w-7 h-7 rounded-full object-cover"
              />
            ) : (
              <div className="w-7 h-7 rounded-full bg-accent/20 flex items-center justify-center text-accent text-sm font-medium">
                {item.name.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{item.name}</div>
              <div className="text-xs text-text-muted truncate">{item.email}</div>
            </div>
            {item.role === 'admin' && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-accent/20 text-accent">
                Admin
              </span>
            )}
          </button>
        ))}
      </div>
    );
  }
);

MentionList.displayName = 'MentionList';
